import './MonitorFormList.scss';

import { AddAppSection } from '../add-app';
import { AsyncStateStatus } from '@sonar-software/xuder';
import { createAsyncSelectors } from '@lib/selectors';
import { FormListFieldData } from 'antd/lib';
import { isEqual } from 'lodash';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { MonitorFormListCard } from './MonitorFormListCard';
import { PlusOutlined } from '@ant-design/icons';
import { SETTINGS_DATA_SECURITY_COPY } from '@sonar-software/sonar-ts-constants';

import { Button, Card, Form, Skeleton, Space } from 'antd';
import {
	coreActions,
	DataSecurityMonitor,
	DataSecurityMonitorType,
	MonitorFormTypes,
	PatchDataSecurityMonitorByIdRequest,
	UptimeMonitor
} from '@sonar-software/sonar-core';
import { defaultConfig, getMenuItems } from '@security/monitors/monitor-form/MonitorMenu';
import { Else, If, Then, When } from 'react-if';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import {
	useAction,
	useAsyncProcessing,
	useCurrentUser,
	useFeatureFlags,
	useFormList,
	useOnMount,
	usePrevious
} from '@hooks';

export interface MonitorFormListProps {
	applicationId: number,
	loading: boolean,
	monitors: Array<MonitorFormTypes>,
	onValuesChange?: () => void;
}

export interface MonitorFormListRef {
	getMonitors: () => Array<MonitorFormTypes>;
	isValid: () => boolean;
	saveAllMonitors: () => void;
	validate: () => void;
}

type InternalFormType = MonitorFormTypes & {
	internalKey?: number
};

const postMonitorSelectors = createAsyncSelectors((state) => state.core.dataSecurity.monitors.post);
const patchMonitorSelectors = createAsyncSelectors((state) => state.core.dataSecurity.monitors.byId.patch);

export const MonitorFormList = forwardRef<MonitorFormListRef, MonitorFormListProps>((
	{
		applicationId,
		loading,
		monitors,
		onValuesChange
	}, ref
) => {
	const postMonitor = useAction(coreActions.requestPostCoreDataSecurityMonitors);
	const resetPostMonitor = useAction(coreActions.resetPostCoreDataSecurityMonitors);
	const patchMonitor = useAction(coreActions.requestPatchCoreDataSecurityMonitorsById);
	const resetPatchMonitor = useAction(coreActions.resetPatchCoreDataSecurityMonitorsById);
	const { isEnabled } = useFeatureFlags();
	const [ adding, setAdding ] = useState<boolean>(false);
	const [ monitorList, setMonitorList ] = useState<Array<InternalFormType>>(monitors ?? []);
	const previousMonitors = usePrevious(monitors);
	const { form } = useFormList<InternalFormType>({
		formListName: 'monitors',
		keyName: 'internalKey',
		required: [ 'monitorConfig.monitorTypeId', 'name' ]
	});
	const menuItems = useMemo(() =>
		getMenuItems(monitorList, isEnabled, [ applicationId ], 'applicationRequired')
	, [ monitorList, applicationId ]);
	const { isReadOnly } = useCurrentUser();

	useOnMount(() => {
		form.validate();
	});

	useEffect(() => {
		if (monitors && !isEqual(monitors, previousMonitors)) {
			const internalMonitors: InternalFormType[] = monitors.map((monitor, index) => ({
				...monitor,
				internalKey: monitor.id || index
			}));

			setMonitorList(internalMonitors);
			form.setFields([
				{
					name: [ 'monitors' ],
					value: internalMonitors
				}
			]);

			form.validate();
		}
	}, [ form, monitors, previousMonitors ]);

	const updateMonitorList = useCallback(() => {
		const existingMonitors = form.getFieldValue('monitors');

		if (existingMonitors) {
			setMonitorList(existingMonitors);

			form.setFields([
				{
					name: [ 'monitors' ],
					value: existingMonitors
				}
			]);
		}

		form.validate();

		if (onValuesChange) {
			onValuesChange();
		}
	}, [ form, onValuesChange ]);

	const handleAddNew = useCallback((addCallback: (defaultValue?:unknown, insertIndex?: number) => void) => {
		const availableMenuItems = menuItems.filter((x) => !x.disabled);
		const autoSelect : InternalFormType =
			availableMenuItems.length === 1 ?
				{ ...availableMenuItems[ 0 ].default } :
				{ ...defaultConfig };

		autoSelect.applicationId = applicationId;

		addCallback(autoSelect);
		setAdding(true);
	}, [ applicationId, menuItems ]);

	const handleValueChange = useCallback(() => {
		form.validate();

		if (onValuesChange) {
			onValuesChange();
		}
	}, [ form, onValuesChange ]);

	const handleCancel = useCallback((
		field: FormListFieldData,
		removeCallback: (index : number | number[]) => void
	) => {
		const monitor = monitorList.find((x) => x.id === form.getItem(field.name).id);

		if (adding && monitorList.length === field.name) {
			removeCallback(field.name);
			setAdding(false);
			updateMonitorList();
		} else if (monitor) {
			form.setFields([
				{
					name: [ 'monitors', field.name ],
					value: monitor
				}
			]);

			form.validate();

			if (onValuesChange) {
				onValuesChange();
			}
		}

	}, [ adding, form, monitorList, onValuesChange, updateMonitorList ]);

	const handleDelete = useCallback((
		field: FormListFieldData,
		removeCallback: (index : number | number[]) => void
	) => {
		removeCallback(field.name);

		updateMonitorList();
	}, [ updateMonitorList ]);

	const handleSave = useCallback((field: FormListFieldData) => {
		if (field.name === monitorList.length && adding) {
			setAdding(false);
		}

		updateMonitorList();
	}, [
		adding,
		monitorList.length,
		setAdding,
		updateMonitorList
	]);

	const handleIsValid = useCallback(() => {
		const currentMonitors = form.getFieldValue([ 'monitors' ]);

		return currentMonitors.every((monitor: InternalFormType) =>
			form.state.find((state) => state.key === monitor.internalKey && !state.isInEdit && state.isValid));
	}, [ form ]);

	const handleOnError = useCallback(() => {
		setMonitorList(monitors);
		form.setFields([
			{
				name: [ 'monitors' ],
				value: monitors
			}
		]);
	}, [ form, monitors ]);

	const postProcess = useAsyncProcessing({
		resetAction: resetPostMonitor,
		stateSelector: postMonitorSelectors.state,
		statusHandlers: {
			[ AsyncStateStatus.Received ]: updateMonitorList,
			[ AsyncStateStatus.Errored ]: handleOnError,
			[ AsyncStateStatus.Failed ]: handleOnError
		}
	});

	const patchProcess = useAsyncProcessing({
		resetAction: resetPatchMonitor,
		stateSelector: patchMonitorSelectors.state,
		statusHandlers: {
			[ AsyncStateStatus.Received ]: updateMonitorList,
			[ AsyncStateStatus.Errored ]: handleOnError,
			[ AsyncStateStatus.Failed ]: handleOnError
		}
	});

	const saveMonitor = useCallback((monitor: MonitorFormTypes) => {
		form.validate();

		if (monitor?.id) {
			const updateMonitor: PatchDataSecurityMonitorByIdRequest = {
				id: monitor.id,
				monitorConfig: {
					enableOnWeekends: monitor.monitorConfig?.monitorTypeId === DataSecurityMonitorType.Uptime ?
						(monitor.monitorConfig as UptimeMonitor['monitorConfig'])?.enableOnWeekends : undefined,
					monitorTypeId: monitor.monitorConfig?.monitorTypeId,
					monitoringWindow: monitor.monitorConfig?.monitorTypeId === DataSecurityMonitorType.Uptime ?
						(monitor.monitorConfig as UptimeMonitor['monitorConfig']).monitoringWindow : undefined
				},
				name: monitor.name,
				notificationSchedule: monitor.notificationSchedule,
				stakeholderIds: monitor.stakeholderIds
			};

			patchProcess.watchNextCall();

			patchMonitor(updateMonitor);

		} else if (applicationId && monitor.monitorConfig?.monitorTypeId && monitor.name) {
			postProcess.watchNextCall();

			postMonitor({
				applicationId: applicationId,
				monitorConfig: monitor.monitorConfig as DataSecurityMonitor['monitorConfig'],
				monitorTypeId: monitor.monitorConfig.monitorTypeId,
				name: monitor.name,
				notificationSchedule: monitor.notificationSchedule,
				stakeholderIds: monitor.stakeholderIds
			});

			setAdding(false);
		}
	}, [ applicationId, form, patchMonitor, postMonitor, patchProcess, postProcess ]);

	const handleSaveAllMonitors = useCallback(() => {
		form.validate();

		const allMonitors = form.getFieldValue('monitors');

		allMonitors.forEach((monitor: MonitorFormTypes) => {
			saveMonitor(monitor);
		});
	}, [ form, saveMonitor ]);

	useImperativeHandle(ref, () => ({
		getMonitors: () =>
			form.getFieldValue('monitors')
				.map((x: InternalFormType) =>
					Object.fromEntries(Object.entries(x).filter(([ key ]) => key !== 'internalKey'))),
		isValid: () => handleIsValid(),
		saveAllMonitors: () => handleSaveAllMonitors(),
		validate: () => form.validate()
	}));

	return (
		<AddAppSection
			className='add-app-monitor-section'
			title={ SETTINGS_DATA_SECURITY_COPY.MONITORS }>
			<If condition={ loading }>
				<Then>
					<LoadingSpinner spinning>
						<Card
							title={ <Skeleton.Input active /> }>
							<Space
								className='monitor-card-body'
								direction='vertical'
								size={ 'large' }>
								<Skeleton.Input active />
								<Skeleton.Node active />
								<Skeleton.Input active />
								<Skeleton.Input active />
							</Space>
						</Card>
					</LoadingSpinner>
				</Then>
				<Else>
					<Form
						className='monitor-form'
						form={ form }
						layout='vertical'
						name='monitorsForm'
						onValuesChange={ handleValueChange }>
						<Form.List
							initialValue={ monitorList }
							name='monitors'>
							{ (fields, { add, remove }) =>
								<>
									{ fields.map((field) =>
										<div key={ field.key }>
											<Form.Item
												hidden
												initialValue={ field.key }
												name={ [ field.name, 'internalKey' ] }
												noStyle />
											<MonitorFormListCard
												fieldId={ field.name }
												form={ form }
												isNew={ adding && monitorList.length === field.name }
												monitors={ monitorList }
												onCancel={ () => handleCancel(field, remove) }
												onDelete={
													monitorList.length === field.name && adding ?
														undefined :
														() => handleDelete(field, remove)
												}
												onEdit={ handleValueChange }
												onSave={ () => handleSave(field) } />
										</div>) }
									<When
										condition={
											!isReadOnly &&
													!adding &&
													menuItems.filter((x) => !x.disabled).length > 0
										}>
										<Form.Item>
											<Button
												className='add-monitor-button'
												icon={ <PlusOutlined /> }
												onClick={ () => handleAddNew(add) }>
												{ SETTINGS_DATA_SECURITY_COPY.ADD_NEW_MONITOR }
											</Button>
										</Form.Item>
									</When>
								</> }
						</Form.List>
					</Form>
				</Else>
			</If>
		</AddAppSection>
	);
});
